/**
 * ---------------------------------------------------------------------------------------------------------------
 * PRODUCT CARD — per-collection experiment behaviour
 * ---------------------------------------------------------------------------------------------------------------
 *
 * Runtime behaviour for the product-card experiments:
 *
 *   1. Swipeable image strip   (women-laptop-bags, bestsellers, work-essentials)
 *   2. Quick-view drawer       (women-laptop-bags, bestsellers)
 *   3. Grid breakers           (work-essentials)
 *
 * Reads <html data-collection data-arm>, set by snippets/experiments.liquid, and does nothing unless the current
 * page's collection is running its test's variant.
 *
 * Vanilla and delegated: one listener per document rather than per card, and no jQuery. A carousel instance per
 * card across a forty-card grid is the main INP risk on this page.
 */

(function () {
  'use strict';

  var SWIPE_COLLECTIONS = ['women-laptop-bags', 'bestsellers', 'work-essentials'];
  // work-essentials is swatches + swipe + grid breakers, with no quick view — see the test matrix in ab-product-card.css.
  var QUICK_VIEW_COLLECTIONS = ['women-laptop-bags', 'bestsellers'];

  function collection() {
    return (window.__growthbook && window.__growthbook.collection) ||
      document.documentElement.getAttribute('data-collection') || '';
  }

  function isVariant() {
    return document.documentElement.getAttribute('data-arm') === 'variant';
  }

  function collectionUses(list) {
    return isVariant() && list.indexOf(collection()) !== -1;
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Swipeable image strip
   * ------------------------------------------------------------------------------------------------------------- */

  /**
   * Build the carousel around an existing media anchor.
   *
   * The server emits only a `data-ab-slides` attribute on .product-card__figure, so arms without swipe keep the
   * stock card structure. That matters beyond the experiment: Swym resolves its wishlist selectors against the
   * stock Prestige card, and app integrations generally assume it.
   *
   * The anchor is moved into the track rather than recreated, and the carousel is inserted at the anchor's former
   * position, so badges and injected app elements keep their place as siblings.
   */
  /**
   * Where the progress bar is parented.
   *
   * FIGMA PUTS IT INSIDE PRODUCT DETAILS ON v4, not in the image. Both frames model it as the details block's FIRST
   * CHILD — a zero-height row whose 2px stroke is drawn UPWARD into the block's own top padding:
   *
   *   WEB   224:21025  Product Details  padding 14 10 10 · gap 12 · content 309 x 172
   *                    224:21026        h-0 w-full, line at inset[-2px 0 0 0]
   *   MWEB  173:12655  Frame 51         padding 8         · gap 12 · content 160.5 x 143
   *                    173:12656        h-0 w-full, line at inset[-2px 0 0 0]
   *
   * The 172 and the 143 are the proof that the row costs nothing: 0 + 12 + 48 + 12 + 52 + 12 + 36 and
   * 0 + 12 + 42 + 12 + 35 + 12 + 30. The 14 and the 8 are PADDING on the block, and the line straddles the inner
   * edge of it.
   *
   * Building the bar in the carousel and reproducing that row with a `::before` got those pixels right and left the
   * TREE wrong — which is the whole of QA V4-002 and V4-009 ("the details container starts below the progress bar")
   * and what keeps V4-003 open, since the run then reads as a 12px MARGIN ON THE BAR where Figma has 14px of
   * PADDING ON THE CONTAINER. So on this arm the bar is parented where the frame parents it, and the CSS drops the
   * stand-in row.
   *
   * NOTHING MOVES ON SCREEN. The bar keeps painting 12 -> 14 below the photo on WEB and 6 -> 8 on MWEB; only the
   * element it hangs off changes.
   *
   * v3 AND v5 KEEP IT IN THE CAROUSEL. Their blocks in ab-product-card.css are measured against a figure that
   * contains the bar — v3's WEB rules in particular cancel its margins to take it back out of the figure's height —
   * and neither arm was raised. Scoped so a bad call costs one collection instead of three.
   */
  function placeProgress(figure, carousel, progress) {
    var root = document.documentElement;

    if (root.getAttribute('data-collection') === 'bestsellers' && root.getAttribute('data-arm') === 'variant') {
      var card = figure.closest('.product-card');
      var info = card && card.querySelector('.product-card__info');
      // First child: Group 1 is the first row of the block in both frames.
      if (info) return info.insertBefore(progress, info.firstChild);
    }

    carousel.appendChild(progress);
  }

  function buildCarousel(figure) {
    if (figure.querySelector('product-card-carousel')) return null;

    var raw = figure.getAttribute('data-ab-slides');
    var anchor = figure.querySelector('a.product-card__media');
    if (!raw || !anchor) return null;

    var sources = raw.split('|||').filter(Boolean);
    if (!sources.length) return null;

    var carousel = document.createElement('product-card-carousel');
    carousel.className = 'product-card__carousel';
    carousel.setAttribute('data-slide-count', String(sources.length + 1));

    var track = document.createElement('div');
    track.className = 'product-card__slides';
    carousel.appendChild(track);

    anchor.parentNode.insertBefore(carousel, anchor);
    track.appendChild(anchor);
    anchor.classList.add('product-card__slide', 'product-card__slide--lead');

    /* EVERY SLIDE IS A LINK TO THE PDP, not just the lead one.
       ------------------------------------------------------------------------------------------------------------
       Slide 1 is the theme's own `a.product-card__media`, moved into the track, so tapping the first photo has
       always opened the product. Slides 2..N were plain divs and swallowed the tap — reported on the v5 breaker,
       whose peek carousel invites a tap on the photo that is half in view.

       AN ANCHOR, NOT A CLICK HANDLER, so the destination is in the DOM: middle-click, long-press "open in new
       tab" and the browser's own status bar all work, and there is no tap-vs-swipe threshold to tune — a touch
       that moves is a scroll and fires no click at all.

       tabindex -1 + aria-hidden, like the carousel arrows: these are the same product at the same URL as the lead
       link, which is already in the tab order and already carries the accessible name. Adding N duplicates of it
       would make the grid N times longer to tab through and read out the same product N times.

       EVERY ARM THAT HAS A STRIP, NOT JUST v5. This first shipped scoped to work-essentials, on the reasoning
       that v3 and v4 were signed off with inert slides. That was the wrong read, and it was reported straight
       back on v3 ("only the first image goes to the PDP"): slide 1 is a link on all three arms because it IS the
       theme's own anchor, so slides 2..N being dead is a defect wherever the strip is built, not a v5 decision.

       Unconditional here, which is exactly the three arms in SWIPE_COLLECTIONS — collectionUses(SWIPE_COLLECTIONS)
       is the only gate buildCarousel runs behind, so this cannot drift out of step with the arms that have a
       strip. WEB and MWEB alike: the markup is the same at both, and nothing overlays the photo but the 32px
       quick-view trigger and Swym's heart in their own corners.

       THE DRAWER'S RECOMMENDATION CARDS COME WITH IT — init() selects figures document-wide, so "You may also
       like" builds the same strip and gets the same links. The drawer's OWN gallery is Liquid-rendered and is
       fixed in sections/quick-view.liquid instead. */
    var slideHref = anchor.getAttribute('href');

    sources.forEach(function (src, i) {
      var slide = document.createElement(slideHref ? 'a' : 'div');
      slide.className = 'product-card__slide';
      if (slideHref) {
        slide.setAttribute('href', slideHref);
        // The theme sets this on its own media anchor: without it a drag on the photo starts a link drag.
        slide.setAttribute('draggable', 'false');
        slide.setAttribute('tabindex', '-1');
        slide.setAttribute('aria-hidden', 'true');
      }
      slide.setAttribute('data-index', String(i + 1));
      slide.setAttribute('data-src', src);
      // Shopify image URLs accept a width param, so a srcset is derived rather than shipped in the attribute.
      slide.setAttribute(
        'data-srcset',
        [400, 600, 800].map(function (w) { return src.replace(/width=\d+/, 'width=' + w) + ' ' + w + 'w'; }).join(', ')
      );
      slide.setAttribute('data-alt', anchor.querySelector('img') ? anchor.querySelector('img').alt : '');
      track.appendChild(slide);
    });

    var progress = document.createElement('div');
    progress.className = 'product-card__progress';
    progress.setAttribute('aria-hidden', 'true');
    progress.innerHTML = '<span class="product-card__progress-fill"></span>';
    placeProgress(figure, carousel, progress);

    // 24x24 caret inside a 32x32 round transparent button.
    var CARET = {
      prev: 'M15 6 L9 12 L15 18',
      next: 'M9 6 L15 12 L9 18'
    };

    ['prev', 'next'].forEach(function (dir) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'product-card__carousel-arrow product-card__carousel-arrow--' + dir;
      button.setAttribute('data-carousel-' + dir, '');
      button.tabIndex = -1;
      button.innerHTML =
        '<span class="sr-only">' + (dir === 'prev' ? 'Previous' : 'Next') + '</span>' +
        '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
        '<path d="' + CARET[dir] + '" stroke="currentColor" stroke-width="1.6" ' +
        'stroke-linecap="round" stroke-linejoin="round"/></svg>';
      carousel.appendChild(button);
    });

    return carousel;
  }

  var ProductCardCarousel = /** @class */ (function () {
    function Carousel(root) {
      this.root = root;
      this.track = root.querySelector('.product-card__slides');
      /* The bar is normally inside this carousel, but v4 parents it to the info panel instead (see placeProgress),
         so the card is the fallback root. Card-scoped, never document-scoped — a bare document lookup on a grid of
         forty carousels would hand every instance the FIRST card's fill and drive one bar from all of them. The
         drawer's own gallery keeps its bar inside the carousel, so it never reaches the fallback. */
      this.fill = root.querySelector('.product-card__progress-fill');
      if (!this.fill) {
        var card = root.closest('.product-card');
        this.fill = card ? card.querySelector('.product-card__progress-fill') : null;
      }
      this.prev = root.querySelector('[data-carousel-prev]');
      this.next = root.querySelector('[data-carousel-next]');
      this.hydrated = false;
      this.index = 0;
      this.maxIndex = 0;
      if (this.track) this.bind();
    }

    Carousel.prototype.slides = function () {
      return Array.prototype.slice.call(this.track.querySelectorAll('.product-card__slide'));
    };

    /**
     * Replace the deferred data-src placeholders with real images. Called once, when the card approaches the
     * viewport — never at page load.
     */
    Carousel.prototype.hydrate = function () {
      if (this.hydrated) return;
      this.hydrated = true;

      this.slides().forEach(function (slide) {
        var src = slide.getAttribute('data-src');
        if (!src || slide.querySelector('img')) return;

        var img = document.createElement('img');
        img.className = 'product-card__image';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.src = src;
        if (slide.getAttribute('data-srcset')) img.srcset = slide.getAttribute('data-srcset');
        img.alt = slide.getAttribute('data-alt') || '';
        slide.appendChild(img);

        slide.removeAttribute('data-src');
        slide.removeAttribute('data-srcset');
      });
    };

    Carousel.prototype.bind = function () {
      var self = this;

      // Hydrate ahead of the viewport rather than on first touch, so the next slide is ready before it is needed.
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              self.hydrate();
              io.disconnect();
            });
          },
          { rootMargin: '300px' }
        );
        io.observe(this.root);
      } else {
        this.hydrate();
      }

      // rAF-throttled: an unthrottled scroll listener across forty carousels saturates the main thread.
      var ticking = false;
      this.track.addEventListener(
        'scroll',
        function () {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(function () {
            self.sync();
            ticking = false;
          });
        },
        { passive: true }
      );

      if (this.prev) this.prev.addEventListener('click', function (e) { e.preventDefault(); self.go(-1); });
      if (this.next) this.next.addEventListener('click', function (e) { e.preventDefault(); self.go(1); });

      this.sync();
    };

    /**
     * Distance from one slide's start to the next.
     *
     * The design puts a gutter between slides (12px on mobile, 16px on desktop, and a peek carousel is narrower
     * than the track), so one step is NOT the track's width. Measured from the slides themselves rather than
     * assumed, which covers the full-width, peek and drawer galleries with one rule.
     */
    Carousel.prototype.step = function () {
      var slides = this.slides();
      if (slides.length > 1) {
        var delta = slides[1].offsetLeft - slides[0].offsetLeft;
        if (delta > 0) return delta;
      }
      return this.track.clientWidth || 1;
    };

    Carousel.prototype.sync = function () {
      var slides = this.slides();
      if (!slides.length) return;

      var width = this.step();
      var index = Math.round(this.track.scrollLeft / width);
      index = Math.max(0, Math.min(index, slides.length - 1));

      /*
       * THE LAST SLIDE OF A PEEK CAROUSEL IS NEVER AT ITS OWN OFFSET.
       *
       * `scrollLeft / step` assumes every slide can be scrolled to the track's left edge. With a peek that is false
       * for the last one: the track runs out of scroll first, so scrollLeft tops out below step x (n - 1) and the
       * index never reaches the end. Measured in the quick view at a 400 panel:
       *
       *     7 slides · step 257 · track 384   ->   last slide's offset 1542, but maxScroll only 1403
       *     scroll-snap then settles at 1373.5, so Math.round(1373.5 / 257) = 5 where the last index is 6
       *
       * The progress bar reads (index + 1) / slides.length, so it stopped at 6/7 — 85.7% — on the final image, and
       * the next arrow stayed enabled with nowhere to go. Both are the same off-by-one.
       *
       * Once the track is within half a step of the end there is nothing further to reach, so that IS the last
       * slide. Half a step rather than an exact match because snapping settles short of maxScroll by design.
       * A full-width carousel is unaffected: there scrollLeft reaches step x (n - 1) exactly, which is already the
       * last index, and this agrees with it.
       */
      /* ...BUT A TRACK THAT CANNOT SCROLL AT ALL IS AT SLIDE 1, NOT AT THE LAST ONE.
       *
       * `scrollWidth - clientWidth` is the whole scroll range. When it is 0 the test above reads 0 - 0 < width/2
       * and declares the carousel finished, so a strip with nothing to scroll reported the LAST index: the bar
       * painted 100% full at rest, `next` came up disabled, and maxIndex started at the end so no image_swipe
       * event could ever fire for that card.
       *
       * WHICH IS THE STATE EVERY v5 CARD BINDS IN. The MWEB rule that removed the standard card's swipe hides
       * slides 2..N with `display: none`, and it is scoped `:not(.product-card--breaker)` — a class
       * markGridBreakers adds AFTER init has built and bound every carousel. So at bind time the breaker is
       * still a standard card by that selector, its slides are display:none, the track measures 0 range, and it
       * latched the last index. Nothing re-synced it afterwards, because a bar at 100% needs no scroll event to
       * stay there: reported as "the progress bar comes fully filled initially".
       *
       * Guarding the range rather than reordering init: the ordering is one of several ways a track can be
       * measured before it is scrollable (a hidden card, a font or image reflow, a filter re-render), and this
       * covers all of them. Once the class lands the slides come back, the range opens up, and the next scroll
       * measures normally — from index 0, which is where the shopper actually is. */
      if (this.track.scrollWidth - this.track.clientWidth > 1 &&
          this.track.scrollWidth - this.track.clientWidth - this.track.scrollLeft < width / 2) {
        index = slides.length - 1;
      }

      this.index = index;
      if (index > this.maxIndex) {
        this.maxIndex = index;
        // One event per newly-reached slide, not per scroll tick — assets/growthbook-events.js sends this as a
        // single image_swipe event, with `context` ('card' or 'quick_view') derived from where `root` lives.
        document.dispatchEvent(new CustomEvent('ab:carousel-swipe', { detail: { root: this.root, index: index } }));
      }

      if (this.fill) {
        // scaleX runs on the compositor; animating width would force layout on every scroll frame.
        this.fill.style.setProperty('--carousel-progress', (index + 1) / slides.length);
        this.fill.parentNode.style.setProperty('--carousel-slide-count', slides.length);
      }
      if (this.prev) this.prev.disabled = index === 0;
      if (this.next) this.next.disabled = index === slides.length - 1;
    };

    Carousel.prototype.go = function (direction) {
      this.hydrate();
      this.track.scrollBy({ left: this.step() * direction, behavior: 'smooth' });
    };

    return Carousel;
  })();

  /* ---------------------------------------------------------------------------------------------------------------
   * Quick-view drawer
   * ------------------------------------------------------------------------------------------------------------- */

  var cache = {};
  var inFlight = {};

  /**
   * Repaint the grid's own wishlist heart for a product the drawer has just saved or unsaved.
   *
   * THE BUG: save a product from the quick view, close it, and the card behind it still showed a hollow heart
   * until the page was reloaded. Nothing was lost — the item was in the wishlist all along — but the grid said
   * otherwise, so it read as a failed save, and shoppers saved the same product twice.
   *
   * WHY: the two hearts have different owners. The card's heart is Swym's, injected and filled in by
   * collectionsApi.initializeCollections, which paints saved state ONCE, when it injects. The drawer's heart is
   * ours, wired straight to Swym's JS API. The API call updates the wishlist itself, and Swym repaints nothing in
   * response — it has no observer and no state-sync hook (the same gap swym-wishlist-rescan.js works around for
   * injection). So the card kept the state it was painted with at load, and only a reload — a fresh
   * initializeCollections against the updated wishlist — could correct it.
   *
   * THE FIX: take the stale heart away and let Swym paint a new one. Re-running initializeCollections over a card
   * that already has a button is not known to re-read its state, but injecting into a card that has none is
   * exactly the path custom.js already relies on after it swaps a card's markup, and the path
   * swym-wishlist-rescan.js measured repairing 219 hearts in one call. Removing the container first puts us on
   * that known-good path.
   *
   * Scoped by data-pid so only the affected card is touched — a blanket repaint would flicker every heart in a
   * 267-card grid. Cards for OTHER colours of the same product are deliberately left alone: each colour is its own
   * product with its own wishlist entry.
   *
   * If Swym declines to re-inject, the original heart goes back where it was. Worst case is then today's
   * behaviour — a stale heart until reload — rather than a card with no heart at all.
   */
  var WISHLIST_REPAINT_VERIFY_DELAY = 700; // matches swym-wishlist-rescan.js: how long a re-scan takes to land

  function repaintCardWishlist(empi) {
    if (!empi) return;
    if (!(window._swat && window._swat.collectionsApi &&
          typeof window._swat.collectionsApi.initializeCollections === 'function')) return;

    var removed = [];

    Array.prototype.forEach.call(
      document.querySelectorAll('.product-card_main[data-pid="' + empi + '"]'),
      function (card) {
        /* The wrapper is what theme.css positions, but the button is the node swym-wishlist-rescan.js actually
           measured, so take the wrapper when there is one and the bare button when there is not. */
        var container = card.querySelector('.swym-wishlist-collections-v2-container') ||
                        card.querySelector('button.swym-wishlist-collections-v2');
        if (!container || !container.parentNode) return;

        removed.push({
          card: card,
          container: container,
          parent: container.parentNode,
          anchor: container.nextSibling
        });
        container.parentNode.removeChild(container);
      }
    );

    if (!removed.length) return;

    try {
      window._swat.collectionsApi.initializeCollections(
        window._swat, true, window.Shopify && Shopify.theme && Shopify.theme.schema_name
      );
    } catch (error) {
      /* fall through to the restore below — a wishlist heart is not worth breaking the grid over */
    }

    setTimeout(function () {
      removed.forEach(function (entry) {
        if (entry.card.querySelector('button.swym-wishlist-collections-v2')) return;

        // Swym re-renders the card's own subtree, so the sibling we noted may be gone by now.
        if (entry.anchor && entry.anchor.parentNode === entry.parent) {
          entry.parent.insertBefore(entry.container, entry.anchor);
        } else if (entry.parent.isConnected) {
          entry.parent.appendChild(entry.container);
        }
      });
    }, WISHLIST_REPAINT_VERIFY_DELAY);
  }

  /**
   * Wishlist inside the quick-view drawer.
   *
   * Swym injects hearts into product cards at load but does not observe DOM added later, and exposes no re-scan
   * method, so the drawer supplies its own button wired directly to the API.
   *
   * Scoped to the drawer only. A card-level button here would duplicate the one Swym injects.
   */
  function bindDrawerWishlist(root) {
    var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-quick-view-wishlist]'));
    if (!buttons.length || !window.SwymCallbacks) return;

    // v3 and v4 place the heart differently, so both are rendered and CSS shows one. Bind every instance and keep
    // them in sync so the visible one is always correct.
    //
    // Swym has already initialised by the time a drawer is opened, but it replaces SwymCallbacks.push with a shim
    // that runs late callbacks immediately, so queueing here is still correct.
    window.SwymCallbacks.push(function (swat) {
      function mark(saved) {
        buttons.forEach(function (button) {
          button.classList.toggle('is-saved', saved);
          button.setAttribute('aria-pressed', saved ? 'true' : 'false');
        });
      }

      function productOf(button) {
        return {
          empi: parseInt(button.getAttribute('data-empi'), 10),
          epi: parseInt(button.getAttribute('data-epi'), 10),
          du: button.getAttribute('data-du')
        };
      }

      // Swym's method names are capital-L: addToWishList / removeFromWishList, and the saved-state check is
      // isInWishlist(product, cb) handing the callback a boolean. There is no addToWishlist or fetchWishlist.
      if (typeof swat.isInWishlist === 'function') {
        swat.isInWishlist(productOf(buttons[0]), function (saved) { mark(!!saved); });
      }

      /* THE CONFIRMATION POPUP: Swym has two add paths and they are NOT equivalent.
       *
       *   addToWishList         instruments itself with channel:"custom-js-api" — readable in its own source. It
       *                         saves the item and renders no storefront UI, because it is the programmatic API.
       *   addToWishlistUsingDu  what Swym's own injected heart calls, through
       *                         collectionsApi.wishlistButtonClickExecution. This is the path that shows
       *                         "Product has been added".
       *
       * The drawer was on the first one, so an add succeeded silently and the shopper got no confirmation — while
       * the same product added from a collection card did show it. Preferring the storefront pair also lets the
       * event carry Swym's own `qv` (quick view) source instead of being lumped into the custom-JS channel.
       *
       * Both UsingDu methods belong to a module that only attaches after Swym registers a session, so they are
       * feature-detected and the plain API stays as the fallback: no popup in that case, but never a dead heart.
       */
      var QUICK_VIEW_SOURCE = swat.wishlistSources && swat.wishlistSources.qv;

      /* The call for this action, or null when Swym exposes neither variant. */
      function resolveAction(saved) {
        if (saved) {
          if (typeof swat.removeFromWishlistUsingDu === 'function') {
            return function (product, onDone, onError) {
              swat.removeFromWishlistUsingDu({ du: product.du, epi: product.epi }, onDone, onError);
            };
          }
          if (typeof swat.removeFromWishList === 'function') {
            return function (product, onDone, onError) { swat.removeFromWishList(product, onDone, onError); };
          }
          return null;
        }

        if (typeof swat.addToWishlistUsingDu === 'function') {
          return function (product, onDone, onError) {
            swat.addToWishlistUsingDu({ du: product.du, source: QUICK_VIEW_SOURCE }, onDone, onError);
          };
        }
        if (typeof swat.addToWishList === 'function') {
          return function (product, onDone, onError) { swat.addToWishList(product, onDone, onError); };
        }
        return null;
      }

      buttons.forEach(function (button) {
        button.addEventListener('click', function () {
          var saved = button.classList.contains('is-saved');
          var action = resolveAction(saved);

          // Bail BEFORE marking. Marking first is what hid the original bug: the name was wrong, so the call was
          // skipped while the heart had already filled in — it looked saved and nothing was ever sent.
          if (!action) {
            console.warn('[ab] Swym exposes no ' + (saved ? 'remove' : 'add') +
                         ' method — the drawer wishlist cannot work until this is remapped.');
            return;
          }

          mark(!saved); // optimistic; reverted if the call fails
          action(
            productOf(button),
            // The wishlist has actually changed by now, so the grid can be repainted from it.
            function () { repaintCardWishlist(button.getAttribute('data-empi')); },
            function () { mark(saved); }
          );
        });
      });
    });
  }

  /**
   * Strip `aria-controls` from every quick-view trigger inside the drawer.
   *
   * <x-drawer> TOGGLES on that attribute (`this.open ? this.hide() : this.show()`), so a trigger carrying it while
   * sitting inside the open drawer closes the panel it is meant to refill. product-card.liquid already omits it for
   * `in_quick_view`, which covers the recommendations as first rendered — but a colour switch on one of those cards
   * does NOT re-render through that path. custom.js swaps the card in place from
   *
   *     GET /products/<handle>?view=product-card
   *
   * which renders product-card.liquid with no `in_quick_view`, so the attribute comes BACK on the fresh markup and
   * the next quick-view click inside the drawer dismisses it. That was the reported bug: switch a colour under
   * "You may also like", then hit quick view, and the drawer vanishes.
   *
   * Normalising the DOM rather than fighting the event: <x-drawer> is shared with the cart and menu drawers and its
   * toggle is bound inside a minified bundle, so the attribute simply must not be there to be read.
   */
  function normaliseDrawerTriggers() {
    var target = document.querySelector('[data-quick-view-target]');
    if (!target) return;

    target.querySelectorAll('[data-quick-view][aria-controls]').forEach(function (trigger) {
      trigger.removeAttribute('aria-controls');
    });
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Never show the previous product
   * ---------------------------------------------------------------------------------------------------------------
   * The drawer opens synchronously through aria-controls, but its content is only replaced once the section
   * request resolves. Leaving the last product in place meant the panel animated in showing it and swapped a
   * beat later — measured at 38 of 55 frames, ~487ms of the wrong product.
   *
   * So the drawer is emptied back to its server-rendered loading state whenever it closes, and again on open if
   * it somehow still holds a different product (the shopper can reopen before the close has finished). Restoring
   * the original markup rather than a hardcoded string keeps the translated loading label.
   * ------------------------------------------------------------------------------------------------------------- */

  var emptyDrawerHTML = null;

  function captureEmptyDrawer() {
    var target = document.querySelector('[data-quick-view-target]');
    if (target && emptyDrawerHTML === null) emptyDrawerHTML = target.innerHTML;
  }

  function resetDrawer() {
    var target = document.querySelector('[data-quick-view-target]');
    if (target && emptyDrawerHTML !== null) target.innerHTML = emptyDrawerHTML;
  }

  /**
   * Record how tall a filled sheet actually is, so the NEXT open reserves that height while it loads.
   *
   * `.quick-view-drawer__loading` reserved a flat 60vh, which is not the height of anything. Measured, the loaded
   * panel is a fixed size per arm and viewport — 0px of spread across five products in each case — and 60vh
   * misses all of them:
   *
   *                       60vh loader    loaded panel     the sheet grows by
   *     v4  414x896          538             608                 +70
   *     v4  360x640          384             563                +179
   *     v3  414x896          538             615                 +77
   *     v3  360x640          384             461                 +77
   *
   * So the sheet animated in at one height and jumped to another after it had already settled — worst on small
   * phones, which is most of the traffic. The two arms disagree and so do the viewports, so there is no constant
   * to hard-code here; reading the real value back off the first fill is both arm- and viewport-correct with no
   * magic numbers, and re-measures itself if either ever changes.
   *
   * Only the first open of a session can still jump. Prefetch usually removes even that, by filling the drawer
   * before it paints a loading state at all.
   */
  function rememberSheetHeight() {
    var content = document.querySelector('.quick-view-drawer__content');
    if (!content) return;

    // After layout, or this measures the panel mid-fill.
    requestAnimationFrame(function () {
      var height = Math.round(content.getBoundingClientRect().height);
      if (height > 0) document.documentElement.style.setProperty('--ab-qv-sheet-height', height + 'px');
    });
  }

  function fillDrawer(html) {
    var target = document.querySelector('[data-quick-view-target]');
    if (!target) return;

    target.innerHTML = html;

    // Gallery inside the drawer is the same component as on the card. Guarded the same way init()'s own
    // .quick-view__gallery binding is: under some trigger sequences (a theme:section:updated dispatch landing
    // between this fill and init()'s next run) both loops reach the same freshly-injected node, and without this
    // flag each would construct its own ProductCardCarousel — two independent scroll listeners on one track,
    // so every swipe fired the tracking event twice.
    target.querySelectorAll('product-card-carousel').forEach(function (node) {
      if (node.dataset.abReady) return;
      node.dataset.abReady = '1';
      new ProductCardCarousel(node);
    });

    bindDrawerWishlist(target);
    rememberSheetHeight();
  }

  /**
   * Mobile presents the drawer as a bottom sheet.
   *
   * <x-drawer> animates itself with a Web Animations API keyframe (translateX -> 0). WAAPI animations outrank CSS
   * animations, so the horizontal one is cancelled and replaced with a vertical entrance.
   */
  function playSheetAnimation() {
    if (!window.matchMedia('(max-width: 699px)').matches) return;

    var drawer = document.getElementById('quick-view-drawer');
    var content = drawer && drawer.shadowRoot && drawer.shadowRoot.querySelector('[part~="content"]');
    if (!content || typeof content.getAnimations !== 'function') return;

    // Every transform animation, not just the horizontal one. Two now need clearing:
    //   · <x-drawer>'s translateX entrance, the original reason this exists;
    //   · the translateY EXIT from the previous close, which fills forwards so the panel stays off-screen until
    //     `display: none` lands. A forwards fill outranks both the inline `transform` below and a later animation
    //     once that one finishes with `fill: none` — so leaving it alive would snap the sheet back off-screen the
    //     instant the entrance completed. Exactly the failure the note below describes, one cycle later.
    content.getAnimations().forEach(function (animation) {
      var frames = animation.effect && animation.effect.getKeyframes ? animation.effect.getKeyframes() : [];
      var transforms = frames.some(function (f) {
        return String(f.transform || f.translate || '').indexOf('translate') !== -1;
      });
      if (transforms) animation.cancel();
    });

    // Pin the resting position BEFORE animating.
    //
    // The cancelled animation was the only thing that would have left the panel at translateX(0); its CSS base
    // state is translateX(100%), i.e. off-screen right. A replacement animation defaults to `fill: none`, so the
    // panel slid up and then snapped straight back off-screen the moment it finished — the drawer read as "open"
    // with a dimmed page and nothing on it.
    //
    // It survived the first open by accident: at rAF time the component had not created its animation yet, so
    // there was nothing to cancel and its own animation ran to completion. Every open after that broke.
    content.style.setProperty('transform', 'none');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    content.animate(
      [{ transform: 'translateY(100%)' }, { transform: 'translateY(0)' }],
      { duration: 280, easing: 'cubic-bezier(0.32, 0.72, 0, 1)' }
    );
  }

  /**
   * ...and mobile closes it the same way, downwards.
   *
   * The sheet used to slide DOWN to open and RIGHT to close. `Drawer` overrides `Modal`'s bottom-sheet pair with a
   * translateX in BOTH directions (theme.js: createEnterAnimationControls / createLeaveAnimationControls), and only
   * the entrance was ever retargeted — so the exit still played the drawer's horizontal one.
   *
   * REPLACED AT SOURCE, not cancelled after the fact like the entrance above. The exit cannot use that trick: the
   * component captures `createLeaveAnimationControls().finished` and only sets `display: none` when it resolves
   * (theme.js:1906). Cancelling a WAAPI animation REJECTS its finished promise, so the panel would never be
   * hidden — it would sit there, animated away but still in the layout, swallowing taps.
   *
   * Overriding the method on the INSTANCE shadows the prototype without touching theme.js, and desktop is handed
   * straight back to the inherited implementation so its horizontal drawer is untouched.
   *
   * `fill: forwards` is what holds the sheet off-screen for the frame between the animation ending and
   * `display: none` landing; without it the transform reverts and the panel flashes back into view. playSheetAnimation
   * clears that fill on the next open.
   */
  function installSheetLeaveAnimation() {
    var drawer = document.getElementById('quick-view-drawer');
    if (!drawer || drawer.abSheetLeaveInstalled) return;
    if (typeof drawer.getShadowPartByName !== 'function') return;   // not upgraded yet
    drawer.abSheetLeaveInstalled = true;

    var inherited = drawer.createLeaveAnimationControls;
    if (typeof inherited !== 'function') return;

    drawer.createLeaveAnimationControls = function () {
      if (!window.matchMedia('(max-width: 699px)').matches) return inherited.call(this);

      var content = this.getShadowPartByName('content');
      if (!content) return inherited.call(this);
      var overlay = this.getShadowPartByName('overlay');

      var timing = {
        duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 280,
        easing: 'cubic-bezier(0.32, 0.72, 0, 1)',
        fill: 'forwards'
      };

      var running = [content.animate([{ transform: 'translateY(0)' }, { transform: 'translateY(100%)' }], timing)];
      if (overlay) running.push(overlay.animate([{ opacity: 1 }, { opacity: 0 }], timing));

      // The shape the caller needs: it reads `.finished` and nothing else on the leave path.
      var finished = Promise.all(running.map(function (animation) { return animation.finished; }));

      // Re-opening inside the 280ms close sends playSheetAnimation through here and cancels these, which rejects
      // the promise. That is the behaviour we want — a rejection means `display: none` is never applied, so the
      // sheet the shopper just re-opened stays visible. The caller attaches only `.then`, so without this the
      // rejection would surface as an unhandled promise error. Observing it here settles that and nothing else:
      // the promise handed back still rejects, so the caller's `.then` correctly does not run.
      finished.catch(function () {});

      return { finished: finished };
    };
  }

  /**
   * Render a product into the open drawer, from cache when it has been seen before.
   *
   * Shared by the card trigger and the drawer's own colour swatches, so a colour change costs the same single
   * section request as the first open and is free once cached.
   */
  /**
   * One request per URL, however many callers want it. A prefetch already in the air is handed to the click that
   * follows it instead of being raced by a second identical request.
   */
  function fetchSection(url) {
    if (cache[url]) return Promise.resolve(cache[url]);
    if (inFlight[url]) return inFlight[url];

    var separator = url.indexOf('?') === -1 ? '?' : '&';
    inFlight[url] = fetch(url + separator + 'section_id=quick-view')
      .then(function (response) {
        if (!response.ok) throw new Error('quick-view ' + response.status);
        return response.text();
      })
      .then(function (html) {
        cache[url] = html;
        delete inFlight[url];
        return html;
      })
      .catch(function (error) {
        delete inFlight[url];
        throw error;
      });

    return inFlight[url];
  }

  /**
   * Warm the cache on intent, without touching the drawer.
   *
   * The panel opens instantly but the section behind it does not: measured against production, a quick-view
   * section render is 217-276ms (the dev server is 585-968ms, so it feels far worse there than it will ship).
   * For that whole window the sheet sits on its loading state and then GROWS when the content lands —
   * `.quick-view-drawer__loading` reserves 60vh, and the loaded panel measured 608px against that 538px, so the
   * sheet animates in at one height and jumps 70px after it has already settled.
   *
   * Prefetching removes both symptoms at once rather than papering over either. `pointerdown` fires before
   * `click` — on touch that gap is the press plus the lift, comfortably more than a production section render —
   * and by the time the click arrives `cache[url]` is populated, so loadQuickView fills the drawer synchronously,
   * before it can paint a loading state at all. `pointerover` gives a pointer device the whole hover.
   *
   * Only ever fires at a finger or cursor already on a trigger, so it costs one request for a card the shopper is
   * in the act of opening — not a speculative sweep of the grid. Skipped outright under Data Saver or 2G, where
   * the wasted request would matter more than the wait it saves.
   */
  function prefetchQuickView(url) {
    if (!url || cache[url] || inFlight[url]) return;

    var connection = navigator.connection;
    if (connection && (connection.saveData || /2g/.test(connection.effectiveType || ''))) return;

    // A warm-up that fails is not an error: the click will retry through loadQuickView and fall back from there.
    fetchSection(url).catch(function () {});
  }

  async function loadQuickView(url) {
    if (!url) return;

    // Prefetched: fill synchronously. No await runs before fillDrawer, so the drawer opens already holding the
    // product — no loading state, and no height jump when it swaps.
    if (cache[url]) {
      fillDrawer(cache[url]);
      return;
    }

    var target = document.querySelector('[data-quick-view-target]');
    if (target) target.setAttribute('aria-busy', 'true');

    try {
      fillDrawer(await fetchSection(url));
    } catch (error) {
      // The drawer is already open via aria-controls, so fall through to the full product page rather than
      // leaving an empty panel.
      window.location.href = url;
    } finally {
      if (target) target.removeAttribute('aria-busy');
    }
  }

  /**
   * Bind the drawer's own lifecycle, so the guarantees hold no matter who opens or closes it.
   *
   * Both hooks correct <x-drawer> from the outside; it is shared with the cart and menu drawers, so it is not
   * patched directly.
   *
   * before-show — THE INVARIANT: a drawer must never become visible holding a previous product.
   *
   *   Opening is decoupled from loading. aria-controls shows the drawer synchronously while the section request
   *   is still in flight, so whatever was rendered last would animate into view and swap a beat later — measured
   *   at 38 of 55 frames, ~487ms of the wrong product.
   *
   *   theme.js dispatches this immediately after `display: block` and before the enter animation is created, so
   *   emptying here lands before the browser can paint. It is bound to the drawer rather than to our trigger on
   *   purpose: every open passes through it, including ones this script did not initiate.
   *
   * after-hide — undo a close that lands after the drawer has already been reopened.
   *
   *   The close finishes asynchronously:
   *
   *       const hideTransitionPromise = this.createLeaveAnimationControls().finished;
   *       hideTransitionPromise.then(() => { this.style.setProperty('display', 'none'); … });
   *
   *   Open another product before that animation ends and the promise still resolves, setting display:none on a
   *   drawer that is now open — `open` with no visible panel and <html class="lock"> still applied, a dimmed and
   *   scroll-locked page with nothing on it. Browsing product to product makes close-then-open the normal rhythm
   *   here, so the race is easy to hit.
   */
  function bindDrawerLifecycle() {
    var drawer = document.getElementById('quick-view-drawer');
    if (!drawer || drawer.dataset.abLifecycleBound) return;
    drawer.dataset.abLifecycleBound = '1';

    drawer.addEventListener('dialog:before-show', function () {
      // Normally already empty, because a genuine close empties it below; this catches a reopen that beat the
      // close's own completion.
      if (drawer.querySelector('[data-quick-view-target] .quick-view')) resetDrawer();
    });

    drawer.addEventListener('dialog:after-hide', function () {
      // display:none is set just before this event, so if we are open again the hide was stale.
      if (drawer.hasAttribute('open')) {
        drawer.style.setProperty('display', 'block');
        return;
      }
      resetDrawer();
    });
  }

  async function openQuickView(trigger) {
    var url = trigger.getAttribute('data-product-url');
    if (!url) return;

    // A trigger inside the drawer ("You may also like") swaps the product in place rather than opening anything:
    // the drawer is already open, and it carries no aria-controls precisely so <x-drawer> does not toggle it shut.
    var drawer = document.getElementById('quick-view-drawer');
    // Containment, not the absence of `aria-controls`. The attribute is the mechanism <x-drawer> reads, not a
    // reliable statement about where the trigger lives — an in-place swap can hand it back (see
    // normaliseDrawerTriggers). Asking whether the trigger is physically inside the open drawer cannot go stale.
    var inPlace = !!drawer && drawer.hasAttribute('open') && drawer.contains(trigger);

    if (inPlace) {
      // dialog:before-show cannot fire for a drawer that never closes, so the invariant it enforces — never show a
      // previous product — has to be applied here. Also drops the outgoing product's recommendations, which the
      // incoming panel refetches for itself.
      resetDrawer();
    } else {
      // A genuine open, not a recommendation swap inside an already-open drawer — this is the one
      // assets/growthbook-events.js counts as quick_view_open.
      document.dispatchEvent(new CustomEvent('ab:quick-view-open', { detail: { trigger: trigger } }));
      // Retarget the exit before it can be needed. Installed here rather than at module load because the override
      // reads `getShadowPartByName` off the upgraded element, and <x-drawer> may not have been defined yet when
      // this file runs. Idempotent, so the repeat calls on later opens cost nothing.
      installSheetLeaveAnimation();
      // the drawer opens via aria-controls before this resolves, so retarget its entrance on the next frame
      requestAnimationFrame(playSheetAnimation);
    }

    await loadQuickView(url);

    // The click came from the foot of a scrolled panel; the new product's title is at the top of it.
    if (inPlace) {
      var body = document.querySelector('[data-quick-view-target] .quick-view__body');
      if (body) body.scrollTop = 0;
    }
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Grid breakers  (v5)
   *
   * Every fifth card spans the full row.
   *
   * :nth-child(5n) cannot express this: the collection grid opens with several `pinned_items` cards that are
   * display:none, and nth-child counts them. Only rendered cards are counted here.
   * ------------------------------------------------------------------------------------------------------------- */

  /* ---------------------------------------------------------------------------------------------------------------
   * Grid breaker white point — REMOVED
   * ---------------------------------------------------------------------------------------------------------------
   * An SVG `feComponentTransfer` used to be injected here and referenced from ab-product-card.css as
   * `filter: url(#ab-breaker-white-point)`, lifting the breaker photo's #E7E7E7 ground to 255 so the featured tile
   * read WHITE against its #F0F0F0 band — Figma's tonal inversion for that card, sampled at 232:1855.
   *
   * Removed on instruction. The CSS rule went with it, and so did the white tile behind it — see "THE WHITE-POINT
   * FILTER IS REMOVED" in ab-product-card.css, which records what the tile reverts to and why the two had to move
   * together. Nothing reads `data-ab-white-point` any more, so the flag is not set either.
   *
   * WORTH KNOWING IF IT COMES BACK: the curve was deliberately top-end-only (identity below 204, ramping 204..231
   * to 255) rather than `filter: brightness()`, which is a linear multiply and dragged product pixels up with the
   * ground — measured over 12 catalogue originals at a mean of +9.9 levels, up to +19 on the pale ones, against
   * +2.2 for the curve with mid-tones bit-identical. `color-interpolation-filters="sRGB"` was mandatory (the SVG
   * default is linearRGB, which puts the ground somewhere else entirely), and the def was injected from JS rather
   * than referenced as a `data:` URI because `filter: url(data:...)` is unreliable in Safari.
   * ------------------------------------------------------------------------------------------------------------- */


  /* ---------------------------------------------------------------------------------------------------------------
   * The breaker's photo needs its own `sizes`, because the breaker is made HERE
   * ---------------------------------------------------------------------------------------------------------------
   * snippets/product-card.liquid builds `sizes` from the section's grid setting, which on mobile is
   *
   *     (max-width: 699px) calc(100vw / 2)      ->  196.5px at a 393 viewport
   *
   * — right for a standard two-up card, and wrong for a breaker, whose peek carousel pins each slide to a fixed
   * 281px (ab-product-card.css, "breaker: peek carousel, 281px slides"). Liquid cannot know which card will
   * become a breaker: that is decided in markGridBreakers below, client-side, long after the attribute is
   * rendered. So the full-bleed card asks the browser for a half-viewport image and the browser obliges.
   *
   * Measured on /collections/work-essentials at 393, before this:
   *
   *     sizes resolves to  196.5      actual slide  281.3      understated by 1.43x
   *     DPR 1  browser wants 196.5 -> picks 200w  into a 281px box
   *     DPR 2  browser wants   393 -> picks 400w            <- the reported "width=400"
   *            it should want  563 -> picks 600w            <- what the tile actually needs
   *
   * ONLY THE MOBILE CLAUSE IS REPLACED. The desktop clauses already OVERSTATE — at 1440 the third clause resolves
   * to 432 against a 326.5 slide — and an overstatement only over-fetches, so they are left exactly as they are
   * and the original list is kept as the fallback after our clause. A `sizes` list is evaluated left to right, so
   * prepending is enough to win at MWEB and to be skipped everywhere else.
   *
   * 281px, NOT A MEASUREMENT. Reading the box back would need re-running on every resize that crosses the
   * breakpoint; the CSS pins this slide to a constant, so the constant is what the attribute should say. If that
   * rule's 281 ever moves, this moves with it.
   *
   * THE DEFERRED SLIDES ARE NOT TOUCHED: buildCarousel gives them no `sizes` at all, so they default to 100vw and
   * over-fetch already. This is the lead image, the one Liquid renders, and the only one that was starved.
   *
   * REVERSIBLE, because markGridBreakers re-runs after a colour swap or a filter change and the 5th slot may land
   * on a different card. The original attribute is stashed on the element so a card that stops being a breaker
   * gets its grid-derived `sizes` back rather than keeping a 281px promise it no longer fills.
   * ------------------------------------------------------------------------------------------------------------- */

  var BREAKER_SLIDE_SIZES = '(max-width: 699px) 281px';

  function syncBreakerImageSizes(card, isBreaker) {
    card.querySelectorAll('.product-card__figure img[srcset]').forEach(function (img) {
      if (isBreaker) {
        if (!('abSizes' in img.dataset)) img.dataset.abSizes = img.getAttribute('sizes') || '';
        var original = img.dataset.abSizes;
        img.setAttribute('sizes', original ? BREAKER_SLIDE_SIZES + ', ' + original : BREAKER_SLIDE_SIZES);
      } else if ('abSizes' in img.dataset) {
        if (img.dataset.abSizes) img.setAttribute('sizes', img.dataset.abSizes);
        else img.removeAttribute('sizes');
        delete img.dataset.abSizes;
      }
    });
  }

  function markGridBreakers() {
    if (!(isVariant() && collection() === 'work-essentials')) return;

    document.querySelectorAll('product-list, .product-list').forEach(function (list) {
      var visible = Array.prototype.filter.call(list.children, function (card) {
        return getComputedStyle(card).display !== 'none';
      });

      visible.forEach(function (card, index) {
        var isBreaker = (index + 1) % 5 === 0;
        card.classList.toggle('product-card--breaker', isBreaker);
        syncBreakerImageSizes(card, isBreaker);
        // assets/growthbook-events.js reads this for the component_visibility event's `position` param.
        if (isBreaker) {
          card.dataset.position = String(index + 1);
        } else {
          delete card.dataset.position;
        }
      });
    });
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Re-attaching after a card is replaced in place
   * ---------------------------------------------------------------------------------------------------------------
   * Choosing a colour swaps the whole card rather than reloading: custom.js fetches
   * /products/<handle>?view=product-card and does
   *
   *     $(this).parents('.product-card_main').html($(data).html())
   *
   * which discards the carousel this script built and fires no event, so there is nothing to listen for. The fresh
   * markup still carries `data-ab-slides`, but with no carousel around it the card loses its strip and progress bar
   * and shows a single static image on a grey ground.
   *
   * Watching the DOM rather than asking custom.js to announce itself keeps the fix inside the experiment's own
   * files — it is removed with them — and it also covers any other in-place swap (apps, third-party filters) that
   * replaces a card without telling anyone.
   * ------------------------------------------------------------------------------------------------------------- */

  var swapObserver = null;
  var scanQueued = false;

  /** Figures the server marked as swipeable that have no carousel around them yet. */
  function unbuiltFigures() {
    return Array.prototype.filter.call(
      document.querySelectorAll('.product-card__figure[data-ab-slides]'),
      function (figure) { return !figure.querySelector('product-card-carousel'); }
    );
  }

  function watchForSwaps() {
    if (!('MutationObserver' in window)) return;

    if (!swapObserver) {
      swapObserver = new MutationObserver(function () {
        // Coalesce a burst of mutations into one check per frame. Swym and lazy images mutate this subtree
        // constantly, so the scan must never run per-record.
        if (scanQueued) return;
        scanQueued = true;
        requestAnimationFrame(function () {
          scanQueued = false;
          // Unconditional, and BEFORE the carousel check: a recommendation card swapped for another colour has to
          // lose its restored `aria-controls` whether or not it also needs a carousel rebuilt. Gating this behind
          // `unbuiltFigures()` would miss any swapped card with a single image.
          normaliseDrawerTriggers();
          // Re-entry is safe: building a carousel removes that figure from this list, so it converges. A figure
          // that cannot be built (no anchor) mutates nothing, so no further records arrive.
          if (unbuiltFigures().length) init();
        });
      });
    }

    // The drawer is watched too: "You may also like" renders real product cards, and custom.js swaps those in
    // place on a colour change exactly as it does on the collection grid — which discards their carousels.
    document.querySelectorAll('product-list, .product-list, [data-quick-view-target]').forEach(function (root) {
      if (root.dataset.abSwapObserved) return;
      root.dataset.abSwapObserved = '1';
      swapObserver.observe(root, { childList: true, subtree: true });
    });
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Boot
   * ------------------------------------------------------------------------------------------------------------- */

  function init() {
    // Snapshot the drawer's server-rendered empty state, then bind its lifecycle, both before anything can open
    // or fill it.
    captureEmptyDrawer();
    bindDrawerLifecycle();

    if (collectionUses(SWIPE_COLLECTIONS)) {
      // Built here rather than in Liquid so non-swipe arms keep the stock card DOM.
      document.querySelectorAll('.product-card__figure[data-ab-slides]').forEach(function (figure) {
        var carousel = buildCarousel(figure);
        if (carousel) new ProductCardCarousel(carousel);
      });

      // Re-arm after a colour swap replaces a card. Idempotent — already-observed lists are skipped.
      watchForSwaps();
    }

    markGridBreakers();

    // The drawer gallery is rendered by Liquid and is always a carousel, whatever the arm.
    document.querySelectorAll('.quick-view__gallery').forEach(function (node) {
      if (node.dataset.abReady) return;
      node.dataset.abReady = '1';
      new ProductCardCarousel(node);
    });

    if (collectionUses(QUICK_VIEW_COLLECTIONS) && !document.documentElement.dataset.abQuickViewBound) {
      document.documentElement.dataset.abQuickViewBound = '1';
      /* Warm the section the moment a finger or cursor lands on a trigger — see prefetchQuickView for why.
         Delegated and passive, so this adds nothing to the tap path itself.

         `pointerover` rather than `pointerenter`: enter does not bubble, so it cannot be delegated. Both events
         are bound because they win at different times — over covers a hover before the press on a pointer
         device, down covers touch, where there is no hover to work with. */
      ['pointerover', 'pointerdown'].forEach(function (type) {
        document.addEventListener(type, function (event) {
          var target = event.target;
          if (!target || typeof target.closest !== 'function') return;
          var trigger = target.closest('.product-card__quick-view-trigger');
          if (trigger) prefetchQuickView(trigger.getAttribute('data-product-url'));
        }, { passive: true });
      });

      /* CAPTURE phase, and this is the load-bearing half of the fix.
       *
       * <x-drawer> binds its toggle as a DELEGATED BUBBLE listener on document.body — theme.js:1709
       * `new Delegate2(document.body)`, wired at 1727 to `[aria-controls="quick-view-drawer"]` — and a delegate
       * evaluates its selector when the event reaches that root. Clearing the attribute during capture therefore
       * lands BEFORE the match runs, so the toggle finds nothing to act on. No stopPropagation, so the bubble
       * handler below still runs and does the in-place swap.
       *
       * Why this is needed at all: the recommendation cards omit `aria-controls` when first rendered
       * (`in_quick_view`), but a colour switch does not re-render through that path — custom.js:716 swaps the card
       * from `/products/<handle>?view=product-card`, and templates/product.product-card.liquid renders the card
       * with no `in_quick_view`, so the attribute comes back. The next quick-view click then hit x-drawer's toggle
       * and dismissed the panel instead of refilling it.
       *
       * normaliseDrawerTriggers() still runs on swap as a belt, but it depends on the MutationObserver having
       * fired, and nothing orders that before the shopper's next click. This does not depend on timing. */
      document.addEventListener('click', function (event) {
        var stale = event.target.closest && event.target.closest('[data-quick-view][aria-controls]');
        if (!stale) return;

        var openDrawer = document.getElementById('quick-view-drawer');
        if (openDrawer && openDrawer.hasAttribute('open') && openDrawer.contains(stale)) {
          stale.removeAttribute('aria-controls');
        }
      }, true);

      // One delegated listener for the whole document — never one per card.
      document.addEventListener('click', function (event) {
        // The MAIN panel's colour swatches come first: each colour is its own product, so the swatch is a plain
        // link to it. custom.js's in-place swap is scoped to `.product-card .thumbnail-swatch` and the panel's own
        // row has no .product-card ancestor, so nothing calls preventDefault and the link navigates away, taking
        // the drawer with it. Swap the panel's contents instead and keep it open, which is what the selected-ring
        // state in the frame implies.
        //
        // Scoped to .quick-view__swatches deliberately. "You may also like" renders real product cards, which
        // DO have a .product-card ancestor, so custom.js already swaps those in place correctly — matching every
        // swatch in the drawer made a recommendation's colour change replace the whole panel with that product.
        var swatch = event.target.closest('[data-quick-view-target] .quick-view__swatches .thumbnail-swatch');
        if (swatch) {
          var colourUrl = swatch.getAttribute('href');
          if (colourUrl) {
            event.preventDefault();
            loadQuickView(colourUrl);
          }
          return;
        }

        var trigger = event.target.closest('[data-quick-view]');
        if (trigger) openQuickView(trigger);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Collection pages paginate and filter without a reload; the theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('theme:section:updated', init);
})();
